import { useEffect, useState } from "react";

/**
 * True once the page is scrolled past `enterAt` pixels, staying true until it
 * comes back above `exitAt`.
 *
 * The two thresholds are not decoration. A caller that collapses part of an
 * in-flow sticky header changes the document height, and Chrome's scroll
 * anchoring compensates by shifting `scrollY` by that same amount to hold the
 * content still. With a single threshold that shift lands on the other side of
 * it, so the state flips, which shifts `scrollY` back, which flips it again —
 * a visible flicker every frame. Keep `enterAt - exitAt` larger than the
 * height the caller adds or removes and the loop cannot close.
 *
 * Deliberately not `useScrollDirection`: that hook calls up to four setStates
 * per scroll event with no throttling, which would re-render the whole header
 * — both mega menus included — on every scroll frame. This reads `scrollY`
 * inside a single rAF and hands React the same boolean while nothing changes,
 * so a render only happens when the threshold is actually crossed.
 *
 * Starts `false` on the server and on the first client render so the SSR
 * markup and the hydrated markup agree; the initial read below then corrects
 * it for a restored scroll position or an `#anchor` entry.
 */
var useIsScrolled = function useIsScrolled() {
  var enterAt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 8;
  var exitAt = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : enterAt;
  var _useState = useState(false),
    isScrolled = _useState[0],
    setIsScrolled = _useState[1];
  useEffect(function () {
    var frame = 0;
    var read = function read() {
      frame = 0;
      var y = window.scrollY;
      // Updater form: React bails out when the boolean is unchanged, so a
      // scroll that stays inside the band costs no render.
      setIsScrolled(function (wasScrolled) {
        return wasScrolled ? y > exitAt : y > enterAt;
      });
    };
    var onScroll = function onScroll() {
      if (frame) return;
      frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return function () {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enterAt, exitAt]);
  return isScrolled;
};
export default useIsScrolled;